"use client";

import {useEffect, useRef, useState} from "react";
import {ChevronDown, Square, SquareCheckBig} from "lucide-react";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {getPreferenceColour} from "./preference-colours";

interface Preference {
    id: string;
    label: string;
}

interface PreferenceFilterBarProps {
    preferences: Preference[];
    selectedIds: Set<string>;
    onToggle: (id: string) => void;
    selectedCountLabel: string;
    selectLabel: string;
}

export function PreferenceFilterBar({preferences, selectedIds, onToggle, selectedCountLabel, selectLabel}: PreferenceFilterBarProps) {
    const [collapsed, setCollapsed] = useState(true);
    const [overflows, setOverflows] = useState(false);
    const summaryRef = useRef<HTMLDivElement>(null);

    // Detect whether the badge summary overflows its container via
    // ResizeObserver. ScrollWidth/clientWidth reads inside the observer
    // callback happen after layout has settled, so they don't force a sync
    // reflow the way a useEffect-on-mount + window-resize listener did.
    useEffect(() => {
        const el = summaryRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            setOverflows(el.scrollWidth > el.clientWidth);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [selectedIds]);

    const selectedPrefs = preferences.filter((p) => selectedIds.has(p.id));

    return (
        <div
            className={`sticky z-20 bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80 ${collapsed ? "" : "border-b border-border"}`}
            style={{top: "var(--header-h, 0px)"}}
        >
            <div className="overflow-x-clip px-4 py-3">
                {/* Mobile: collapsible header */}
                <div className="flex items-center gap-2 sm:hidden">
                    <button
                        type="button"
                        onClick={() => setCollapsed((c) => !c)}
                        className="flex min-w-0 flex-1 items-center gap-2"
                        aria-expanded={!collapsed}
                    >
                        {collapsed && selectedPrefs.length === 0 ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1 text-sm font-medium">
                                <ChevronDown className="size-4 shrink-0 transition-transform" />
                                {selectLabel}
                            </span>
                        ) : (
                            <>
                                <ChevronDown
                                    className={`size-4 shrink-0 transition-transform ${collapsed ? "" : "rotate-180"}`}
                                />
                                {collapsed && selectedPrefs.length > 0 ? (
                                    overflows ? (
                                        <span className="text-sm text-muted-foreground">
                                            {selectedCountLabel}
                                        </span>
                                    ) : (
                                        <div ref={summaryRef} className="flex min-w-0 flex-1 gap-1 overflow-hidden">
                                            {selectedPrefs.map((p) => {
                                                const colour = getPreferenceColour(preferences.indexOf(p));
                                                return (
                                                    <Badge key={p.id} variant="secondary" className="shrink-0 text-xs" style={{borderColor: colour, backgroundColor: `oklch(from ${colour} l c h / 0.1)`}}>
                                                        {p.label}
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    )
                                ) : (
                                    <span className="text-sm text-muted-foreground">
                                        {selectedCountLabel}
                                    </span>
                                )}
                            </>
                        )}
                    </button>
                </div>

                {/* Mobile: expanded content / Desktop: always visible */}
                <div
                    className={`${collapsed ? "hidden" : "mt-3"} sm:mt-0 sm:block`}
                    role="group"
                >
                    <div className="flex flex-wrap gap-2">
                        {preferences.map((pref, index) => {
                            const isSelected = selectedIds.has(pref.id);
                            const colour = getPreferenceColour(index);
                            return (
                                <Button
                                    key={pref.id}
                                    type="button"
                                    role="switch"
                                    aria-checked={isSelected}
                                    onClick={() => onToggle(pref.id)}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full"
                                    style={isSelected ? {borderColor: colour, backgroundColor: `oklch(from ${colour} l c h / 0.1)`} : undefined}
                                >
                                    <span className="inline-flex size-4 items-center justify-center" style={isSelected ? {color: colour} : undefined}>
                                        {isSelected ? (
                                            <SquareCheckBig className="size-4" />
                                        ) : (
                                            <Square className="size-4 opacity-30" />
                                        )}
                                    </span>
                                    {pref.label}
                                </Button>
                            );
                        })}
                    </div>
                </div>

                {/* Hidden measurement div for overflow detection */}
                <div
                    ref={summaryRef}
                    className="pointer-events-none invisible absolute flex gap-1 overflow-hidden"
                    aria-hidden="true"
                    style={{maxWidth: "calc(100% - 4rem)"}}
                >
                    {selectedPrefs.map((p) => {
                        const colour = getPreferenceColour(preferences.indexOf(p));
                        return (
                            <Badge key={p.id} variant="secondary" className="shrink-0 text-xs" style={{borderColor: colour, backgroundColor: `oklch(from ${colour} l c h / 0.1)`}}>
                                {p.label}
                            </Badge>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
